import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FLAT_STEP_CONFIGS } from '@/features/guided-financial-planning/config';

/**
 * Hook to sync react-use-wizard with react-router.
 * Returns startIndex and onStepChange props for the Wizard component.
 */
export function useWizardRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial step from URL
  const pathSegments = location.pathname.split('/');
  const currentStepPath = pathSegments[pathSegments.length - 1];
  const startIndex = Math.max(
    0,
    FLAT_STEP_CONFIGS.findIndex(step => step.path === currentStepPath),
  );

  // Update URL when step changes, preserving query params
  const onStepChange = useCallback(
    (stepIndex: number) => {
      const stepPath = FLAT_STEP_CONFIGS[stepIndex]?.path;
      if (!stepPath) return;

      const segments = location.pathname.split('/');
      const lastSegment = segments[segments.length - 1];
      const isValidStep = FLAT_STEP_CONFIGS.some(s => s.path === lastSegment);

      const newPath = isValidStep
        ? [...segments.slice(0, -1), stepPath].join('/')
        : `${location.pathname}/${stepPath}`;

      // Check if we're leaving a step that should be skipped in history
      const currentStepConfig = FLAT_STEP_CONFIGS.find(s => s.path === lastSegment);
      const shouldReplace = currentStepConfig?.skipHistory ?? false;

      // Preserve query params when navigating between steps
      navigate({ pathname: newPath, search: location.search }, { replace: shouldReplace });
    },
    [location.pathname, location.search, navigate],
  );

  return { startIndex, onStepChange };
}
